import { getCurrentTenant, useDescope, useSession, useUser } from '@descope/react-sdk'
import { Navigate, useLocation } from 'react-router-dom'

import { AppRoute } from '@/lib/url/urls'

import { UnauthorizedAccess } from '@/pages/error-pages/unauthorized-access/unauthorized-access'

import { Loading } from '@/components/lemmala-design-system/loading/loading'

import { PermissionKey, useHasPermissions } from './check-permissions.hook'

interface ProtectedRouteProps {
  requiredPermissions?: PermissionKey[]
  children: React.ReactNode
  loginPath?: string
}

export const ProtectedRoute = ({
  requiredPermissions,
  children,
  loginPath = '/login',
}: ProtectedRouteProps) => {
  const location = useLocation()
  const hasPermissions = useHasPermissions(requiredPermissions)
  const hasBaseView = useHasPermissions(['base.view'])
  const { user, isUserLoading } = useUser()
  const { isAuthenticated, isSessionLoading, sessionToken } = useSession()
  const descope = useDescope()
  if (!isAuthenticated && !isSessionLoading) {
    return (
      <Navigate
        to={loginPath}
        state={{
          from: { pathname: location.pathname, search: location.search, hash: location.hash },
        }}
      />
    )
  }
  if (isSessionLoading || isUserLoading) {
    return <Loading />
  }

  if (isAuthenticated && user && !isSessionLoading && !isUserLoading) {
    const tenant = getCurrentTenant(sessionToken)
    const userTenants = user.userTenants
    if (userTenants && userTenants.length > 0 && tenant === '') {
      descope.selectTenant(userTenants[0].tenantId)
    }
  }

  if (!hasPermissions) {
    // Portal-only roles (e.g. Business Owner) have no main-platform access. Send them
    // straight to the intake portal instead of a permission-error page (ENG-3345).
    if (!hasBaseView) {
      return <Navigate to={AppRoute.COMPANY_REQUEST} replace />
    }
    return <UnauthorizedAccess />
  }

  return <>{children}</>
}
